












































































































































































import { defineComponent, ref, computed, watch, PropType, Ref, toRefs, nextTick } from '@vue/composition-api';
import SearchKeywordsUpload from '../search-keywords-upload/index.vue';
import { cloneDeep, findLastIndex, uniq } from 'lodash-es';
import { ValidationError, useAdgroupMode } from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import { KeywordAuditStatus, SearchAdsKeywordItem, KeywordInvalidCode, CreateMode } from '@ad/tt_ads_common_enums';
import { useSearchKeywords } from '../hook';
import { Rule } from 'async-validator';
import SearchKeywordsInput from './search-keywords-input.vue';
import { checkIsKeywordListEqual, getErrorMessage, trackEvent } from '@tt4b/ttam-utils';
import { FormItem } from 'byted-vui';
import { KEYWORDS_USAGE_IMG_URL } from '../const';

const name = 'ttam-search-keywords';
export default defineComponent({
  name,
  components: { SearchKeywordsInput, SearchKeywordsUpload },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    createMode: {
      type: String as PropType<CreateMode>,
      default: CreateMode.CREATE,
    },
    maxInputNumber: {
      type: Number,
      default: 1000,
    },
    maxKeywordLength: {
      type: Number,
      default: 80,
    },
    searchKeywordList: {
      type: Array as PropType<SearchAdsKeywordItem[]>,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { createMode } = toRefs(props);
    const { isEdit } = useAdgroupMode({ createMode });

    function onKeywordsChange(val) {
      emit('on-keywords-change', val);
    }

    const {
      keywordList,
      repeatKeywords,
      inputRefs,
      searchKeywordRefs,
      handleInputKeywords,
      deleteKeywordList,
      batchAddKeywords,
      inputFocusOnLastKeywordItem,
      clearAllKeywords,
      addKeyword,
      validateKeywordItem,
    } = useSearchKeywords(onKeywordsChange);
    const focusedId = ref('');
    const hoveredId = ref('');
    const uploadDialogVisible = ref(false);

    const repeatKeywordsString = computed(() => uniq(repeatKeywords.value).join(', '));
    const isShowDisapprovedInfo = computed(() => isEdit.value);
    const disapprovedKeywordItemList = computed(() => {
      if (!isShowDisapprovedInfo.value) {
        return [];
      }

      return keywordList.value.filter(
        item => item.keywordAuditStatus === KeywordAuditStatus.REJECTED && Boolean(item.rejectReasons.length),
      );
    });

    const rules: Ref<Rule> = computed(() => [
      {
        validator: (rule, value, callback) => {
          const field = rule.field || '';
          const index = Number(field.split('.')[1]);

          /** input框最大数量，前端控制 */
          if (keywordList.value.length > props.maxInputNumber && index === keywordList.value.length - 1) {
            return callback(
              new ValidationError({
                messageI18nKey: 'adgroup_search_keywords_valid_exceed_max_number',
                message: i18n.t('adgroup_search_keywords_valid_exceed_max_number', { num: props.maxInputNumber }),
                moduleName: i18n.t('adgroup_search_keywords'),
                subModuleName: i18n.t('adgroup_search_keywords'),
              }),
            );
          }

          return callback();
        },
      },
      {
        validator: (rule, value, callback) => {
          const field = rule.field || '';
          const index = Number(field.split('.')[1]);

          const currentKeywordItem = keywordList.value[index];
          if (!currentKeywordItem) {
            return callback();
          }

          const { invalidCode } = currentKeywordItem;
          const errorMessage = getErrorMessage(invalidCode);

          if (errorMessage) {
            return callback(
              new ValidationError({
                messageI18nKey: errorMessage,
                message: i18n.t(errorMessage),
                moduleName: i18n.t('adgroup_search_keywords'),
                subModuleName: i18n.t('adgroup_search_keywords'),
              }),
            );
          }

          return callback();
        },
      },
      {
        validator: (rule, value, callback) => {
          const field = rule.field || '';
          const index = Number(field.split('.')[1]);

          const currentKeywordItem = keywordList.value[index];
          if (!currentKeywordItem) {
            return callback();
          }

          const { keyword } = currentKeywordItem;

          const emptyErrorMessage = getErrorMessage(KeywordInvalidCode.EMPTY_WORD);
          if (!keyword) {
            return callback(
              new ValidationError({
                messageI18nKey: emptyErrorMessage,
                message: i18n.t(emptyErrorMessage),
                moduleName: i18n.t('adgroup_search_keywords'),
                subModuleName: i18n.t('adgroup_search_keywords'),
              }),
            );
          }

          return callback();
        },
      },
      {
        validator: (rule, value, callback) => {
          const field = rule.field || '';
          const index = Number(field.split('.')[1]);

          const currentKeywordItem = keywordList.value[index];
          if (!currentKeywordItem) {
            return callback();
          }

          const { keywordId } = currentKeywordItem;

          const systemErrorMessage = getErrorMessage(KeywordInvalidCode.SYSTEM_ERROR);
          if (!keywordId) {
            return callback(
              new ValidationError({
                messageI18nKey: systemErrorMessage,
                message: i18n.t(systemErrorMessage),
                moduleName: i18n.t('adgroup_search_keywords'),
                subModuleName: i18n.t('adgroup_search_keywords'),
              }),
            );
          }

          return callback();
        },
        trigger: 'submit',
      },
    ]);

    /** 事件监听用户操作 ----------------------------------- */
    function onInputFocus(index: number) {
      const currentItem = keywordList.value[index];
      if (currentItem) {
        focusedId.value = currentItem.id;
      }
    }

    function onInputBlur(index: number) {
      const currentItem = keywordList.value[index];
      if (currentItem) {
        focusedId.value = currentItem.id;
      }
    }

    function onInputMouseEnter(index: number) {
      const currentItem = keywordList.value[index];
      if (currentItem) {
        hoveredId.value = currentItem.id;
      }
    }

    function onInputMouseLeave() {
      hoveredId.value = '';
    }

    function onUploadKeywords(keywords: string[]) {
      if (!keywords.length) {
        return;
      }

      /** 上传时删除尾部几个空白的input */
      const lastHasContentIndex = findLastIndex(keywordList.value, item => item.keyword !== '');
      deleteKeywordList(lastHasContentIndex + 1);

      batchAddKeywords(keywordList.value.length, keywords);
    }

    function onDeleteKeywordClick(index: number) {
      /** 剩余最后一个input框时交互与clear all一致 */
      if (keywordList.value.length === 1) {
        clearAllKeywords();

        return;
      }

      deleteKeywordList(index, 1);
    }

    function onClearAllClick() {
      clearAllKeywords();
    }

    function onInput(index: number, val: string) {
      /** 先设置成val，避免原来input框的值和下面格式化之后的值相同，丢失响应式 */
      keywordList.value[index].keyword = val;
      onKeywordsChange(keywordList.value);
    }

    function onKeywordChange(index: number, val: string) {
      handleInputKeywords(index, val);
    }

    function onKeyDown(index: number, e: KeyboardEvent) {
      if (e.key === 'Enter' && e.keyCode === 13) {
        e.preventDefault();
        batchAddKeywords(index + 1, ['']);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const lastIndex = Math.max(0, index - 1);
        const lastKeywordItem = keywordList.value[lastIndex];
        inputFocusOnLastKeywordItem([lastKeywordItem]);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = Math.min(index + 1, keywordList.value.length - 1);
        const nextKeywordItem = keywordList.value[nextIndex];
        inputFocusOnLastKeywordItem([nextKeywordItem]);
      }
    }

    function onAddKeywordClick() {
      addKeyword();
    }

    function onUploadKeywordClick() {
      uploadDialogVisible.value = true;
    }

    function onUploadDialogClose() {
      uploadDialogVisible.value = false;
    }

    function onAuditItemClick(keywordItem: SearchAdsKeywordItem) {
      const index = keywordList.value.findIndex(item => item.id === keywordItem.id);
      const currentItem = keywordList.value[index];

      if (currentItem) {
        const eventParams = {
          std_component_name: 'module_ad_group_keywords_audit_item',
          keywords: currentItem.keyword,
        };

        trackEvent('creation_component_click', eventParams);
        if ((searchKeywordRefs.value[index] as FormItem)?.$el?.scrollIntoView) {
          (searchKeywordRefs.value[index] as FormItem)?.$el?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    }

    /** ---------------------------------------------------- */

    /** 外部数据同步内部数据 */
    watch(
      () => props.searchKeywordList,
      val => {
        const isKeywordsEqual = checkIsKeywordListEqual(val, keywordList.value);

        keywordList.value = cloneDeep(val);
        // 关键词变化，校验新Keywords
        if (!isKeywordsEqual) {
          nextTick(() => {
            keywordList.value.forEach((item, index) => validateKeywordItem(index));
          });
        }
      },
      { immediate: true },
    );

    return {
      name,
      keywordList,
      hoveredId,
      focusedId,
      inputRefs,
      searchKeywordRefs,
      repeatKeywordsString,
      uploadDialogVisible,
      rules,
      isShowDisapprovedInfo,
      disapprovedKeywordItemList,
      KEYWORDS_USAGE_IMG_URL,
      onAddKeywordClick,
      onUploadKeywordClick,
      onInput,
      onKeyDown,
      onInputFocus,
      onInputBlur,
      onInputMouseEnter,
      onInputMouseLeave,
      onDeleteKeywordClick,
      onClearAllClick,
      onUploadKeywords,
      onUploadDialogClose,
      onAuditItemClick,
      onKeywordChange,
    };
  },
});
